import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useDownloadCSV } from '../../hooks';
import { ReplenishmentInvoiceReportTableParams } from '../ReplenishmentInvoiceReport.types';

export const QUERY_KEY = 'EXPORT_REPLENISHMENT_INVOICE_REPORT_CSV';
const useReplenishmentInvoiceReportExportCSVQuery = (params: ReplenishmentInvoiceReportTableParams) => {
  const handleSaveFile = useDownloadCSV();
  const [isLoading, setIsLoading] = useState(false);

  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const { refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_REPLENISHMENT_INVOICE_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          employer_id: params.employerId,
          show_ssn: params.isSSNShowed,
          invoice_close_start_date: formattedStartDate,
          invoice_close_end_date: formattedEndDate,
          invoice_id: params.invoiceID,

          search_value: params.searchString,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response?.data?.data, 'csv', 'replenishment-invoice_report');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);
  return {
    isLoading,
    handleExport,
  };
};

export default useReplenishmentInvoiceReportExportCSVQuery;
